import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { TrendingUp, TrendingDown, Minus, Bell, BellOff, Plus, Trash2, Loader2, Sparkles, RefreshCw, CheckCircle, AlertCircle, Lock, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';

export const AssetValuation = ({ assetId, assetName, currentValue, category, purchasePrice, onValueUpdate }) => {
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const [valuation, setValuation] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [alertForm, setAlertForm] = useState({ alert_type: 'above', target_value: '' });
  
  // AI Valuation state
  const [aiValuation, setAiValuation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [hasAIAccess, setHasAIAccess] = useState(null);

  useEffect(() => {
    fetchData();
    checkAIAccess();
  }, [assetId]);

  const checkAIAccess = async () => {
    try {
      const response = await api.getSubscriptionAccess();
      setHasAIAccess(response.data?.access?.ai_insights || false);
    } catch (error) {
      setHasAIAccess(false);
    }
  };

  const fetchData = async () => {
    try {
      const [valuationRes, alertsRes] = await Promise.all([
        api.getAssetValuation(assetId),
        api.getAlerts()
      ]);
      setValuation(valuationRes.data);
      setAlerts(alertsRes.data.filter(a => a.asset_id === assetId));
    } catch (error) {
      console.error('Failed to fetch valuation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = async (e) => {
    e.preventDefault();
    try {
      await api.createAlert({
        asset_id: assetId,
        alert_type: alertForm.alert_type,
        target_value: parseFloat(alertForm.target_value)
      });
      toast.success('Price alert created');
      setShowAlertDialog(false);
      setAlertForm({ alert_type: 'above', target_value: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create alert');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await api.deleteAlert(alertId);
      toast.success('Alert deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  // AI Valuation Functions
  const requestAIValuation = async () => {
    setAiLoading(true);
    try {
      const response = await api.getAIValuation({
        name: assetName,
        category: category || 'art',
        purchase_price: purchasePrice || currentValue,
        current_value: currentValue,
        description: `${assetName} - Luxury ${category} asset`
      });
      setAiValuation(response.data);
      toast.success('AI valuation complete');
    } catch (error) {
      console.error('AI Valuation error:', error);
      if (error.response?.status === 403) {
        toast.error('AI Valuation requires Professional or Elite membership');
      } else {
        toast.error('Failed to get AI valuation');
      }
    } finally {
      setAiLoading(false);
    }
  };

  const applyAIValuation = async () => {
    if (!aiValuation?.estimated_value) return;
    
    setUpdating(true);
    try {
      await api.updateAsset(assetId, {
        current_value: aiValuation.estimated_value
      });
      toast.success('Asset value updated with AI estimate');
      if (onValueUpdate) {
        onValueUpdate(aiValuation.estimated_value);
      }
      // Refresh valuation data
      fetchData();
    } catch (error) {
      toast.error('Failed to update asset value');
    } finally {
      setUpdating(false);
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'text-emerald-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-orange-400';
      default: return 'text-muted-foreground';
    }
  };

  const getConfidenceBg = (confidence) => {
    switch (confidence) {
      case 'high': return 'bg-emerald-400/10 border-emerald-400/30';
      case 'medium': return 'bg-yellow-400/10 border-yellow-400/30';
      case 'low': return 'bg-orange-400/10 border-orange-400/30';
      default: return 'bg-white/5 border-white/10';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp size={20} className="text-emerald-400" />;
    if (trend === 'down') return <TrendingDown size={20} className="text-red-400" />;
    return <Minus size={20} className="text-muted-foreground" />;
  };

  const getTrendColor = (trend) => {
    if (trend === 'up') return 'text-emerald-400';
    if (trend === 'down') return 'text-red-400';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="luxury-card p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-gold animate-spin" />
      </div>
    );
  }

  if (!valuation) return null;

  // Format chart data
  const chartData = valuation.valuation_history.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    value: item.value
  }));

  return (
    <div className="space-y-6">
      {/* AI Valuation Section */}
      <div className="luxury-card p-6" data-testid="ai-valuation-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-gold" />
            <h2 className="font-serif text-xl text-white">AI Valuation</h2>
          </div>
          {hasAIAccess && (
            <Button 
              onClick={requestAIValuation}
              disabled={aiLoading}
              className="btn-secondary flex items-center gap-2"
              data-testid="request-ai-valuation-btn"
            >
              {aiLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Analysing...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  Get AI Estimate
                </>
              )}
            </Button>
          )}
        </div>

        {/* Upgrade Required for Starter Users */}
        {hasAIAccess === false && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-gold" />
            </div>
            <h3 className="font-serif text-xl text-white mb-2">Upgrade to Unlock AI Valuation</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Get AI-powered market valuations for your assets with a Professional or Elite membership. 
              Our AI analyses current market trends, auction results, and comparable sales.
            </p>
            <Link to="/pricing">
              <Button className="btn-primary flex items-center gap-2 mx-auto">
                <Crown size={16} />
                Upgrade Membership
              </Button>
            </Link>
          </div>
        )}

        {/* AI Valuation Content for Users with Access */}
        {hasAIAccess && aiValuation ? (
          <div className="space-y-4">
            {/* AI Estimated Value */}
            <div className={`p-4 border ${getConfidenceBg(aiValuation.confidence)}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">AI Estimated Value</p>
                  <p className="text-3xl font-serif text-white">{formatCurrency(aiValuation.estimated_value)}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Range: {formatCurrency(aiValuation.value_range_low)} - {formatCurrency(aiValuation.value_range_high)}
                  </p>
                </div>
                <div className={`px-3 py-1 border ${getConfidenceBg(aiValuation.confidence)}`}>
                  <span className={`text-xs uppercase tracking-wider ${getConfidenceColor(aiValuation.confidence)}`}>
                    {aiValuation.confidence} confidence
                  </span>
                </div>
              </div>
            </div>

            {/* Comparison with Current Value */}
            {currentValue && (
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#0A0A0A] border border-[#1A1A1A]">
                  <p className="text-xs text-muted-foreground mb-1">Current Value</p>
                  <p className="text-lg text-white">{formatCurrency(currentValue)}</p>
                </div>
                <div className="p-3 bg-[#0A0A0A] border border-[#1A1A1A]">
                  <p className="text-xs text-muted-foreground mb-1">Difference</p>
                  <p className={`text-lg ${aiValuation.estimated_value >= currentValue ? 'text-emerald-400' : 'text-red-400'}`}>
                    {aiValuation.estimated_value >= currentValue ? '+' : ''}
                    {formatCurrency(aiValuation.estimated_value - currentValue)}
                  </p>
                </div>
              </div>
            )}

            {/* AI Reasoning */}
            <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A]">
              <p className="text-xs text-gold uppercase tracking-wider mb-2">AI Analysis</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{aiValuation.reasoning}</p>
            </div>

            {/* Market Factors */}
            {aiValuation.market_factors && aiValuation.market_factors.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Key Market Factors</p>
                <div className="flex flex-wrap gap-2">
                  {aiValuation.market_factors.map((factor, index) => (
                    <span key={index} className="px-3 py-1 bg-gold/10 border border-gold/30 text-xs text-gold">
                      {factor}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Apply Valuation Button */}
            <Button 
              onClick={applyAIValuation}
              disabled={updating}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="apply-ai-valuation-btn"
            >
              {updating ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Accept
                </>
              )}
            </Button>
          </div>
        ) : hasAIAccess && (
          <div className="text-center py-8">
            <Sparkles size={32} className="text-gold/50 mx-auto mb-3" />
            <p className="text-muted-foreground mb-2">
              Get an AI-powered market valuation for this asset
            </p>
            <p className="text-xs text-muted-foreground">
              Our AI analyses current market trends, comparable sales, and category-specific factors
            </p>
          </div>
        )}
      </div>

      {/* Valuation Overview */}
      <div className="luxury-card p-6" data-testid="valuation-overview">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-xl text-white">Market Valuation</h2>
          <div className="flex items-center gap-2">
            {getTrendIcon(valuation.market_trend)}
            <span className={`text-sm font-medium ${getTrendColor(valuation.market_trend)}`}>
              {valuation.trend_percentage >= 0 ? '+' : ''}{valuation.trend_percentage}%
            </span>
          </div>
        </div>

        {/* Price Chart */}
        <div className="h-48 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888', fontSize: 10 }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888', fontSize: 10 }}
                tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                width={60}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#0F0F0F', 
                  border: '1px solid #262626',
                  borderRadius: 0
                }}
                labelStyle={{ color: '#888' }}
                formatter={(value) => [formatCurrency(value), 'Value']}
              />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke="#D4AF37" 
                strokeWidth={2}
                fill="url(#valueGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <p className="text-xs text-muted-foreground">
          Last updated: {new Date(valuation.last_updated).toLocaleString()}
        </p>
      </div>

      {/* Comparable Sales */}
      {valuation.comparable_sales && valuation.comparable_sales.length > 0 && (
        <div className="luxury-card p-6" data-testid="comparable-sales">
          <h3 className="font-serif text-lg text-white mb-4">Comparable Sales</h3>
          <div className="space-y-3">
            {valuation.comparable_sales.map((sale, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#1A1A1A]"
              >
                <div>
                  <p className="text-sm text-white">{sale.item}</p>
                  <p className="text-xs text-muted-foreground">{sale.source} • {sale.date}</p>
                </div>
                <p className="text-sm text-gold font-mono">{formatCurrency(sale.sale_price)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price Alerts */}
      <div className="luxury-card p-6" data-testid="price-alerts-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-gold" />
            <h3 className="font-serif text-lg text-white">Price Alerts</h3>
          </div>
          <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-gold hover:text-gold/80" data-testid="add-alert-btn">
                <Plus size={16} className="mr-1" /> Add Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F0F0F] border-[#262626]">
              <DialogHeader>
                <DialogTitle className="font-serif text-white">Create Price Alert</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAlert} className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Alert Type</Label>
                  <Select 
                    value={alertForm.alert_type}
                    onValueChange={(value) => setAlertForm({...alertForm, alert_type: value})}
                  >
                    <SelectTrigger className="luxury-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-[#262626]">
                      <SelectItem value="above">Price Goes Above</SelectItem>
                      <SelectItem value="below">Price Falls Below</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-muted-foreground">Target Value ($)</Label>
                  <Input
                    type="number"
                    value={alertForm.target_value}
                    onChange={(e) => setAlertForm({...alertForm, target_value: e.target.value})}
                    placeholder={currentValue?.toString()}
                    className="luxury-input"
                    required
                  />
                </div>
                <Button type="submit" className="btn-primary w-full">Create Alert</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {alerts.length > 0 ? (
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div 
                key={alert.id}
                className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#1A1A1A]"
                data-testid={`alert-${alert.id}`}
              >
                <div className="flex items-center gap-3">
                  {alert.is_active ? (
                    <Bell size={14} className="text-gold" />
                  ) : (
                    <BellOff size={14} className="text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm text-white">
                      {alert.alert_type === 'above' ? 'Price above' : 'Price below'} {formatCurrency(alert.target_value)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Created {new Date(alert.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteAlert(alert.id)}
                  className="text-muted-foreground hover:text-red-400 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            No price alerts set. Create one to get notified of market movements.
          </p>
        )}
      </div>
    </div>
  );
};
